import React from 'react'
import { 
	Link,
	Prompt,
	Redirect,
	Route,
	Switch,
} from 'react-router-dom'

import moment from 'moment';

import { 
	Field, 
	Form,
} from 'formik'

import { Debug } from '@components/Debug/Debug'
import Loading from '@components/Loading/Loading'
import DataExportButton from '@components/DataExportButton/DataExportButton'
import DataTable from '@components/DataTable/DataTable'
import PageHeaderSticky from '@components/PageHeaderSticky/PageHeaderSticky'
import { 
	FormGroup, 
	FieldGroup, 
	FormField 
} from '@modules/form/form.module'
import { calculateWeeksWorked } from '@utils/weeks'
import { calculateRate } from '@utils/rates'
import statuses from '@server/constants/statuses/timesheet-statuses'

import './TimesheetSingular.scss'

function sumHours(hoursArray) {
	return hoursArray.reduce((a,b) => a+(b?b:0), 0);
}

// TODO: import these functions from isomorphic tax-week.js

class TimesheetSingularMain extends React.Component {

	async submitTimesheet() {
		const { setFieldValue, submitForm } = this.props;
		await setFieldValue('status', statuses.find(s => s.key === 'submitted'));
		submitForm();
	}

	async approveTimesheet() {
		const { setFieldValue, submitForm } = this.props;
		await setFieldValue('status', statuses.find(s => s.key === 'approved'));
		submitForm();
	}

	async resetStatus(e) {
		const { setFieldValue, submitForm } = this.props;
		e.preventDefault();
		await setFieldValue('status', statuses.find(s => s.key === 'live'));
		submitForm();
	}

	render() {
		var { 
			isEditing, 
			isNew,  
			dirty, 
			formConfig, 
			data, 
			values, 
			mapDataToCSV,
			mapDataToCSVFilename,
		} = this.props;
		
		data = data ? data : {};
		const client = data.client ? data.client : {};

		return (
			<main className="Singular page-content">

				<Prompt 
					when={isEditing && dirty ? true : false}
					message={location => `Changes will be lost if you navigate away\n\nare you sure you want to leave this page?`}
				/>
			
				<PageHeaderSticky title={`Timesheet: ${client.label}`}>
					{ 
						data.status && data.status.key === 'live' && 
						<button className="btn btn-primary" onClick={::this.submitTimesheet}>Submit</button> 
					}
					{ 
						data.status && data.status.key === 'submitted' && 
						<button className="btn btn-primary" onClick={::this.approveTimesheet}>Approve</button> 
					}
					{
						mapDataToCSV && !isNew && !isEditing
						&& <DataExportButton data={data} mapDataToCSV={mapDataToCSV} mapDataToCSVFilename={mapDataToCSVFilename} />
					}
				</PageHeaderSticky>

				<section className="PageContent">
					
					<Form>
						{formConfig.map((group, i) =>
							<FormGroup key={i} {...group} values={values} isEditing={isEditing} />
						)}
	        </Form>

	        <DataTable 
	        	data={data.timesheetLines}
        		columns= {[
        			{
        				label: 'Name',
        				getValue: (line) => line.candidate.label,
        			},
        			{
        				label: 'Weeks Worked',
        				getValue: (line) => {
        					const startDate = line.candidate.clientStartDates.find(o => o.client === data.client._id).startDate;
        					return `${calculateWeeksWorked(startDate, data.weekEnding)} (Started ${moment(startDate).format('DD/MM/YYYY')})`;
        				}
        			},
        			{
        				label: 'Payroll No.',
        				getValue: (line) => line.candidate.payrollNo,
        			},
        			{
        				label: 'Job Type',
        				getValue: (line) => line.jobType.label,
        			},
        			{
        				label: 'Rate',
        				getValue: (line) => {
        					var rate = calculateRate(data.client, line.candidate, data.weekEnding, line.jobType._id);
        					return isNaN(rate) ? rate : `£${rate}`;
        				}
        			},
        			{
        				label: 'Total Hours',
        				getValue: (line) => line.totalHours,
        			},
        			{
        				label: 'Total Cost',
        				getValue: (line) => !isNaN(line.totalCost) ? `£${line.totalCost}` : '-',
        			}
        		]}
	        	rowLinks= {(line) => (
	        		<div>
	        			<Link className="TableRow-link" to={`/timesheet/${data._id}/line/${line._id}`}>view</Link>
	        		</div>
	        	)}
	        />

	        <div className="Totals">
	        	<p><b>Totals</b></p>
	        	<p>Total Hours: {data.totalHours} hours</p>
	        	<p>Total Cost: £{data.totalCost}</p>
        </div>

				</section>

				{ process.env.NODE_ENV === 'development' &&
		    	<Debug />
		    }
		    { process.env.NODE_ENV === 'development' &&
					<button type="button" onClick={::this.resetStatus}>reset</button> 
		    }
			</main>
		)
	}
}

class TimesheetLine extends React.Component {

	get timesheetLine() {
		return this.props.data.timesheetLines[this.timesheetIndex];
	}

	////
	// Lifecycle
	////
	
	constructor(props) {
		super(props);
		this.timesheetIndex = props.data.timesheetLines.findIndex(line => line._id === props.match.params.lineId);
		this.state = { 
			updateLineTotals: false,
			updateTimesheetTotals: false,
		}
	}

	componentWillUnmount() {
		this.props.resetForm();
	}

	componentDidUpdate() {

		if( this.state.updateLineTotals ) {
			const { setFieldValue, values, data } = this.props;
			const currentLine = values.timesheetLines[this.timesheetIndex];

			const linePath = `timesheetLines.${this.timesheetIndex}`;


			// calculate new line totals
			const totalLineHours = sumHours(values.timesheetLines[this.timesheetIndex].hours);			
			const rate = calculateRate(data.client, currentLine.candidate, data.weekEnding, currentLine.jobType._id);

			// update field values
			setFieldValue(`${linePath}.totalHours`, totalLineHours);
			setFieldValue(`${linePath}.totalCost`, rate * totalLineHours);

			// TODO: update parent timesheet totals


			// set updateLineTotals flag 
			this.setState({ 
				updateLineTotals: false,
				updateTimesheetTotals: true,
			})
		}

		// TODO: update totals in form onChange instead of waiting for componentDidUpdate twice
		if(this.state.updateTimesheetTotals) {
			const { values, setFieldValue } = this.props;
			// calculate timesheet totals
			const timesheetTotals = values.timesheetLines.reduce((totals, line) => ({
				totalHours: totals.totalHours + line.totalHours,
				totalCost: totals.totalCost + line.totalCost
			}), { totalHours: 0, totalCost: 0 });

			setFieldValue('totalCost', timesheetTotals.totalCost);
			setFieldValue('totalHours', timesheetTotals.totalHours);

			this.setState({
				updateTimesheetTotals: false,
			})

		}

	}



	render() {
		const { timesheetIndex } = this;
		const { data, dirty, submitForm, handleChange } = this.props;

		if(timesheetIndex === -1) 
			return <Redirect to={`/timesheet/${data._id}`} /> 

		return (
			<main className="Singular page-content"> 
				
				<Prompt 
					when={dirty}
					message={location => `Changes will be lost if you navigate away.\nare you sure you want to leave this page?`}
				/>

				<PageHeaderSticky title={`Week Ending: ${moment(data.weekEnding).format('DD/MM/YYYY')}, Tax Week: ${data.taxWeek}`}>
					<button className="btn btn-primary" onClick={::this.backToTimesheet}>back</button> 
					<button type="submit" onClick={submitForm} disabled={!dirty}>update</button> 
				</PageHeaderSticky>
		
				<Form>
					<FieldGroup 
						label="Name" 
						name={`timesheetLines.${this.timesheetIndex}.candidate.label`} 
						isEditing={false}
					/>
					<FieldGroup 
						label='Payroll Number' 
						name={`timesheetLines.${this.timesheetIndex}.candidate.payrollNo`}
						isEditing={false} 
					/>
					<FieldGroup 
						label='Job Type' 
						name={`timesheetLines.${this.timesheetIndex}.jobType.label`} 
						isEditing={false}
					/>
					<FieldGroup 
						label='Client Name' 
						name={`client.label`} 
						isEditing={false}
					/>

					<FormField
						label="Hours"
						component="Hours"
						name={`timesheetLines.${this.timesheetIndex}.hours`}
						isEditing={true}
						onChange={e => {
							handleChange(e);
							this.setState({updateLineTotals: true});							
						}}
					/>

					<Field
						name={`timesheetLines.${this.timesheetIndex}.totalHours`}
						component={({field}) => <p><b>Total Hours: {field.value ? field.value : 0} hours</b></p>}
						isEditing={false}
					/>

					{/*<b>Total: {values.timesheetLines[this.timesheetIndex].totalHours} hours</b>*/}
		    </Form>

		    { process.env.NODE_ENV === 'development' &&
		    	<Debug />
		    }
		
			</main>
		)
		
	}

	////
	// Event Handlers
	////

	backToTimesheet() {
		const { match, history } = this.props;
		history.push(`/timesheet/${match.params.timesheetId}`);
	}

}

export default 
class TimesheetSingular extends React.Component {

	render() {

		var { values } = this.props;
	
		return !values || !Object.keys(values).length
			? <Loading isLoading={true} />
			: (
				<Switch>
					<Route exact path="/timesheet/:timesheetId/line/:lineId" render={({match}) => <TimesheetLine {...this.props} match={match} />} />
					<Route render={() => <TimesheetSingularMain {...this.props} />} />
				</Switch>
			)
	
	}

}




// WEBPACK FOOTER //
// ./src/scenes/Timesheets/TimesheetSingular.jsx